import { useCallback, useEffect, useReducer, useRef } from 'react'
import FileSaver from 'file-saver'
import { useNotify } from 'react-admin'
import { useTranslation } from 'react-i18next'

import Loadable, { TLoadableAny } from 'src/utils/Loadable'
import type { TAlarmReportProps } from 'src/parser/shared/PDF/Provider'

import { collectAlarmArchiveEntries, zipEntries } from '../Export/alarmArchive'
import type { TArchiveEntry } from '../Export/alarmArchive'
import {
  alarmPrefix,
  mergeOutcomes,
  multiArchiveName,
  failedAlarmOutcome,
} from '../Export/multiAlarmArchive'
import type { TAlarmOutcome } from '../Export/multiAlarmArchive'
import {
  INITIAL_EXPORT_STATE,
  exportReducer,
  TExportAction,
  TExportState,
} from '../Export/exportState'
import { toExportError } from '../Export/exportErrors'
import type { TExportFailure } from '../Export/exportErrors'
import {
  RUNNING_NOTICE_DELAY,
  notifyFailure,
  notifyOutcome,
  notifyRunning,
} from '../Export/exportNotifications'
import type { TNotify, TTranslate } from '../Export/exportNotifications'

const CONSOLIDATED_REPORT_NAME = 'report.pdf'

const messageOf = (cause: unknown): string =>
  cause instanceof Error ? cause.message : JSON.stringify(cause)

const collectMedia = async (
  report: TAlarmReportProps,
): Promise<TAlarmOutcome> => {
  const alarmId = report.entity.id
  const { attachments, additionalDetails } = report

  const { entries, failures, addEntry } = await collectAlarmArchiveEntries({
    alarmId,
    prefix: alarmPrefix(alarmId),
    attachments: Loadable.isSuccess(attachments) ? attachments.data : [],
    additionalDetails: Loadable.isSuccess(additionalDetails)
      ? additionalDetails.data
      : {},
  })

  const collected: TExportFailure[] = [...failures]

  failures.forEach(({ name, cause }) => {
    addEntry(`ERROR-${name ?? 'media'}.txt`, cause)
  })

  const loaders: Array<[string, TLoadableAny | undefined]> = [
    ['attachments', attachments],
    ['additional_details', additionalDetails],
  ]

  loaders.forEach(([label, loadable]) => {
    if (loadable == null) return
    if (!Loadable.isFailure(loadable)) return

    const cause = messageOf(loadable.error)
    addEntry(`ERROR_${label}.txt`, cause)
    collected.push({ code: 'REPORT_DATA_INCOMPLETE', alarmId, cause })
  })

  return { entries, failures: collected }
}

type TRunContext = {
  reports: TAlarmReportProps[]
  pdfBlob: Blob | null
  notify: TNotify
  t: TTranslate
}

const runExport = async (
  { reports, pdfBlob, notify, t }: TRunContext,
  dispatch: (action: TExportAction) => void,
): Promise<void> => {
  if (pdfBlob === null) return

  dispatch({ type: 'START', total: reports.length })

  let runningNotice: ReturnType<typeof setTimeout> | undefined = setTimeout(
    () => {
      runningNotice = undefined
      notifyRunning(notify, t)
    },
    RUNNING_NOTICE_DELAY,
  )

  try {
    const outcomes: TAlarmOutcome[] = []

    for (const report of reports) {
      try {
        outcomes.push(await collectMedia(report))
      } catch (cause) {
        outcomes.push(failedAlarmOutcome(report.entity.id, messageOf(cause)))
      }
      dispatch({ type: 'PROGRESS' })
    }

    const { entries, failures } = mergeOutcomes(outcomes)
    const consolidated: TArchiveEntry = {
      name: CONSOLIDATED_REPORT_NAME,
      content: pdfBlob,
    }
    const fileName = multiArchiveName(reports.length)

    let archive: Blob
    try {
      archive = await zipEntries([consolidated, ...entries])
    } catch (cause) {
      const error = toExportError(cause, 'ARCHIVE_FAILED')
      dispatch({ type: 'FAILURE', error })
      notifyFailure(notify, t, error)
      return
    }

    try {
      FileSaver.saveAs(archive, fileName)
    } catch (cause) {
      const error = toExportError(cause, 'SAVE_FAILED')
      dispatch({ type: 'FAILURE', error })
      notifyFailure(notify, t, error)
      return
    }

    dispatch({ type: 'SUCCESS', fileName, failures })
    notifyOutcome(notify, t, failures)
  } catch (cause) {
    const error = toExportError(cause, 'UNKNOWN')
    dispatch({ type: 'FAILURE', error })
    notifyFailure(notify, t, error)
  } finally {
    if (runningNotice !== undefined) clearTimeout(runningNotice)
  }
}

export type TUseBulkDownloadZipResult = [() => void, TExportState]

export const useBulkDownloadZip = (
  reports: TAlarmReportProps[],
  pdfBlob: Blob | null,
): TUseBulkDownloadZipResult => {
  const [state, dispatch] = useReducer(exportReducer, INITIAL_EXPORT_STATE)
  const notify = useNotify()
  const { t } = useTranslation()

  const runningRef = useRef(false)
  const latest = useRef<TRunContext>({ reports, pdfBlob, notify, t })

  useEffect(() => {
    latest.current = { reports, pdfBlob, notify, t }
  })

  const downloadZip = useCallback((): void => {
    if (runningRef.current) return
    if (latest.current.reports.length === 0) return
    if (latest.current.pdfBlob === null) return

    runningRef.current = true

    runExport(latest.current, dispatch)
      .catch(() => undefined)
      .finally(() => {
        runningRef.current = false
      })
  }, [])

  return [downloadZip, state]
}

export default useBulkDownloadZip
